import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, Button, Paper, Avatar, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, UserGroupIcon, ScaleIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SwipeIcon from '@mui/icons-material/Swipe';

// Import court images
import allahabadCourt from '../assets/images/allahabad.jpg';
import supremeCourt from '../assets/images/supremecourt.jpg';

// Custom arrow components for slider
const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: 'absolute',
        left: { xs: '10px', md: '20px' },
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        bgcolor: 'rgba(255, 255, 255, 0.3)',
        color: 'white',
        width: { xs: '40px', md: '50px' },
        height: { xs: '40px', md: '50px' },
        '&:hover': {
          bgcolor: 'rgba(255, 255, 255, 0.5)',
        },
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'all 0.3s ease',
        display: { xs: 'none', md: 'flex' }
      }}
      aria-label="Previous slide"
    >
      <KeyboardArrowLeftIcon sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' } }} />
    </IconButton>
  );
};

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: 'absolute',
        right: { xs: '10px', md: '20px' },
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        bgcolor: 'rgba(255, 255, 255, 0.3)',
        color: 'white',
        width: { xs: '40px', md: '50px' },
        height: { xs: '40px', md: '50px' },
        '&:hover': {
          bgcolor: 'rgba(255, 255, 255, 0.5)',
        },
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'all 0.3s ease',
        display: { xs: 'none', md: 'flex' }
      }}
      aria-label="Next slide"
    >
      <KeyboardArrowRightIcon sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' } }} />
    </IconButton>
  );
};

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showSwipeIndicator, setShowSwipeIndicator] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const whatsappNumber = "+919455133862"; // Replace with your actual WhatsApp number

  // Court images for the banner slider
  const courtImages = [
    {
      img: supremeCourt,
      title: "Excellence in Legal Solutions",
      description: "Providing strategic legal counsel to businesses for over two decades. We combine deep legal expertise with business acumen to deliver results that matter."
    },
    {
      img: allahabadCourt,
      title: "Not just A solution but THE Solution",
      description: "We don't just offer legal services—we deliver strategic, results-driven solutions tailored to your business needs. From company formation to complex litigation, we are THE solution you've been looking for."
    }
  ];

  // Hide swipe indicator after first slide on mobile
  useEffect(() => {
    if (isMobile) {
      const timer = setTimeout(() => {
        setShowSwipeIndicator(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  // Banner slider settings
  const bannerSliderSettings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    fade: true,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    cssEase: "cubic-bezier(0.7, 0, 0.3, 1)",
    beforeChange: (current, next) => setCurrentSlide(next),
    appendDots: dots => (
      <Box 
        sx={{ 
          position: 'absolute',
          bottom: { xs: '15px', sm: '20px', md: '25px' },
          width: '100%',
          padding: 0,
          margin: 0,
          listStyle: 'none',
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <ul style={{ margin: '0px', padding: 0 }}>{dots}</ul>
      </Box>
    ),
    customPaging: i => (
      <Box
        sx={{
          width: { xs: '8px', sm: '10px', md: '12px' },
          height: { xs: '8px', sm: '10px', md: '12px' },
          margin: '0 4px',
          backgroundColor: i === currentSlide ? 'white' : 'rgba(255,255,255,0.4)',
          borderRadius: '50%',
          display: 'inline-block',
          transition: 'all 0.3s ease',
          transform: i === currentSlide ? 'scale(1.2)' : 'scale(1)',
          boxShadow: i === currentSlide ? '0 0 8px rgba(255,255,255,0.8)' : 'none'
        }}
        role="button"
        aria-label={`Go to slide ${i + 1}`}
      />
    ),
    responsive: [
      {
        breakpoint: 600,
        settings: {
          arrows: false,
          autoplaySpeed: 4000,
        }
      }
    ]
  };

  const services = [
    {
      title: "Corporate Law & Governance",
      description: "Expert guidance in business formation, mergers & acquisitions, corporate restructuring, and regulatory compliance. We help businesses navigate complex legal requirements while maintaining operational efficiency.",
      features: ["Business Formation", "M&A Advisory", "Corporate Restructuring", "Regulatory Compliance"]
    },
    {
      title: "Intellectual Property Rights",
      description: "Comprehensive IP protection services including patent filing, trademark registration, and copyright protection. We safeguard your innovations and creative works in the digital age.",
      features: ["Patent Applications", "Trademark Registration", "Copyright Protection", "IP Litigation"]
    },
    {
      title: "Business Litigation",
      description: "Strategic representation in complex commercial disputes, contract conflicts, and business torts. Our experienced litigators provide robust advocacy while prioritizing your business interests.",
      features: ["Commercial Disputes", "Contract Litigation", "Business Torts", "Alternative Dispute Resolution"]
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      position: "CEO, Tech Innovations Inc",
      content: "The legal team provided exceptional guidance during our company's merger. Their expertise made a complex process seamless."
    },
    {
      name: "Michael Chen",
      position: "Founder, StartUp Solutions",
      content: "Their intellectual property team helped secure our patents efficiently. Highly recommended for tech companies."
    },
    {
      name: "Emily Rodriguez",
      position: "Legal Director, Global Corp",
      content: "Outstanding service and attention to detail. They've been instrumental in our international expansion efforts."
    },
    {
      name: "David Park",
      position: "CFO, Finance Solutions Ltd",
      content: "Their corporate law expertise has been invaluable. They consistently deliver practical, business-focused solutions."
    }
  ];

  const features = [
    {
      icon: ShieldCheckIcon,
      title: "Expert Counsel",
      desc: "Seasoned attorneys with industry expertise"
    },
    {
      icon: UserGroupIcon,
      title: "Client Focus",
      desc: "Personalized attention to your needs"
    },
    {
      icon: ScaleIcon,
      title: "Proven Track Record",
      desc: "Consistent success in complex cases"
    },
    {
      icon: LightBulbIcon,
      title: "Innovative Solutions",
      desc: "Modern approach to legal challenges"
    }
  ];

  const handleWhatsAppClick = (service) => {
    const message = `Hi, I'm interested in your ${service.title} services. I would like to schedule a free consultation.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Mobile-optimized slider settings
  const serviceSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: isMobile ? 1 : isTablet ? 2 : 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    arrows: !isMobile,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true
        }
      }
    ]
  };

  const testimonialSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: isMobile ? 1 : 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: !isMobile,
    responsive: [
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false
        }
      }
    ]
  };

  return (
    <>
      {/* Image Slider Section */}
      <Box sx={{ position: 'relative' }} className="hero-slider">
        {/* Progress indicator */}
        <Box 
          sx={{
            position: 'absolute', 
            top: 0, 
            left: 0, 
            height: '4px', 
            width: '100%',
            zIndex: 5,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{
              height: '100%',
              backgroundColor: 'secondary.main',
              width: '100%',
              animation: 'slideProgress 6s linear infinite',
              '@keyframes slideProgress': {
                '0%': { width: '0%' },
                '100%': { width: '100%' }
              },
              animationPlayState: 'running',
              '.hero-slider:hover &': {
                animationPlayState: 'paused'
              }
            }}
          />
        </Box>

        <Slider {...bannerSliderSettings}>
          {courtImages.map((item, index) => (
            <Box key={index} sx={{ position: 'relative' }}>
              <Box sx={{ 
                height: { xs: '60vh', sm: '65vh', md: '75vh', lg: '85vh' },
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.7) 100%)',
                  zIndex: 1
                }
              }}>
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${item.img})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  transition: 'transform 10s ease-in-out',
                  animation: `zoom${index} 15s infinite alternate`,
                  '@keyframes zoom0': {
                    '0%': { transform: 'scale(1)' },
                    '100%': { transform: 'scale(1.1)' }
                  },
                  '@keyframes zoom1': {
                    '0%': { transform: 'scale(1.05)' },
                    '100%': { transform: 'scale(1.15)' }
                  }
                }}/>
                
                <Container maxWidth="lg" sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',  
                  position: 'relative',
                  zIndex: 2
                }}>
                  <Box sx={{ 
                    width: '100%',
                    maxWidth: { xs: '90%', sm: '80%', md: '800px' },
                    textAlign: 'center',
                    animation: 'fadeIn 1.2s ease-in-out',
                    '@keyframes fadeIn': {
                      '0%': { opacity: 0, transform: 'translateY(30px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        color: 'white',
                        fontWeight: 800,
                        fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem', lg: '4rem' },
                        textShadow: '2px 2px 8px rgba(0,0,0,0.7)',
                        mb: { xs: 2, md: 3 },
                        lineHeight: 1.2,
                        mx: 'auto',
                        animation: 'slideInUp 0.8s ease-out',
                        '@keyframes slideInUp': {
                          '0%': { opacity: 0, transform: 'translateY(40px)' },
                          '100%': { opacity: 1, transform: 'translateY(0)' }
                        },
                        animationDelay: '0.3s',
                        animationFillMode: 'both'
                      }}
                    >
                      {item.title}
                    </Typography>
                    
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'white',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                        textShadow: '1px 1px 4px rgba(0,0,0,0.7)',
                        mb: { xs: 3, md: 4 },
                        mx: 'auto',
                        maxWidth: '700px',
                        px: { xs: 2, sm: 0 },
                        lineHeight: 1.5,
                        animation: 'slideInUp 0.8s ease-out',
                        '@keyframes slideInUp': {
                          '0%': { opacity: 0, transform: 'translateY(40px)' },
                          '100%': { opacity: 1, transform: 'translateY(0)' }
                        },
                        animationDelay: '0.6s',
                        animationFillMode: 'both'
                      }}
                    >
                      {item.description}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      animation: 'slideInUp 0.8s ease-out',
                      '@keyframes slideInUp': {
                        '0%': { opacity: 0, transform: 'translateY(40px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' }
                      },
                      animationDelay: '0.9s',
                      animationFillMode: 'both'
                    }}>
                      <Button
                        component={Link}
                        to="/contact"
                        variant="contained"
                        color="secondary"
                        size={isMobile ? "medium" : "large"}
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                          px: { xs: 3, md: 5 },
                          py: { xs: 1.2, md: 1.5 },
                          boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Consult with Our Experts
                      </Button>
                    </Box>
                  </Box>
                </Container>
                
                {/* Slide counter for desktop */}
                <Box 
                  sx={{
                    position: 'absolute',
                    bottom: { md: '30px' },
                    right: { md: '30px' },
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    backdropFilter: 'blur(4px)',
                    padding: '8px 15px',
                    borderRadius: '20px',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    zIndex: 5,
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  {`${currentSlide + 1} / ${courtImages.length}`}
                </Box>
              </Box>
            </Box>
          ))}
        </Slider>
        
        {/* Mobile swipe indicator */}
        {isMobile && showSwipeIndicator && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '60px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(4px)',
              borderRadius: '20px',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              zIndex: 10,
              animation: 'fadeInOut 3s infinite',
              '@keyframes fadeInOut': {
                '0%': { opacity: 0 },
                '50%': { opacity: 1 },
                '100%': { opacity: 0 }
              }
            }}
          >
            <SwipeIcon sx={{ color: 'white', fontSize: '1rem' }} />
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
              Swipe to explore
            </Typography>
          </Box>
        )}
      </Box>

      {/* Firm Overview Section - Mobile Optimized */}
      <Box className="bg-gradient-to-br from-gray-50 to-gray-100" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container sx={{ justifyContent: 'center' }}>
            {/* Your Trusted Legal Partner section */}
            <Grid item xs={12}>
              <Box sx={{ 
                px: { xs: 1, sm: 2, md: 4 }, 
                mb: 4,
                pt: { xs: 1, sm: 2, md: 3 }
              }} className="text-center">
                <Typography 
                  variant="h3" 
                  className="font-heading"
                  sx={{ 
                    fontWeight: 800,
                    color: 'primary.main',
                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                    mb: { xs: 2, md: 3 },
                    letterSpacing: '-0.01em'
                  }}
                >
                  Your Trusted Legal Partner
                </Typography>
                
                <Typography 
                  className="font-sans"
                  sx={{ 
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    mb: { xs: 3, md: 4 },
                    maxWidth: '800px',
                    mx: 'auto',
                    px: { xs: 1, sm: 0 }
                  }}
                >
                  Since 2000, our firm has been at the forefront of providing sophisticated legal solutions 
                  to businesses across industries. Our team of experienced attorneys combines deep legal 
                  knowledge with practical business insight.
                </Typography>

                <Grid container spacing={{ xs: 2, md: 3 }} justifyContent="center">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Box sx={{
                          p: { xs: 2, md: 2.5 },
                          bgcolor: 'white',
                          borderRadius: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          mx: 'auto',
                          maxWidth: { xs: '100%', sm: '250px' },
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease'
                          }
                        }}>
                          <Box sx={{ 
                            width: { xs: 48, md: 56 },
                            height: { xs: 48, md: 56 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            mb: { xs: 2, md: 2.5 },
                            color: 'white'
                          }}>
                            <Icon style={{ width: isMobile ? 24 : 28, height: isMobile ? 24 : 28 }} />
                          </Box>
                          <Typography 
                            variant="h6" 
                            className="font-heading"
                            sx={{ 
                              fontWeight: 600,
                              mb: 1,
                              fontSize: { xs: '1rem', md: '1.125rem' },
                              color: 'text.primary'
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            className="font-sans"
                            sx={{
                              color: 'text.secondary',
                              lineHeight: 1.6,
                              fontSize: { xs: '0.825rem', md: '0.875rem' }
                            }}
                          >
                            {feature.desc}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: { xs: 3, md: 6 } }}>
                <Grid container spacing={{ xs: 2, md: 4 }} sx={{ 
                  maxWidth: { xs: '100%', lg: '1200px' },
                  justifyContent: 'center',
                  mx: 'auto'
                }}>
                  {[
                    { number: '20+', text: 'Years of Experience' },
                    { number: '500+', text: 'Successful Cases' },
                    { number: '50+', text: 'Expert Attorneys' },
                    { number: '300+', text: 'Active Clients' }
                  ].map((stat, index) => (
                    <Grid item xs={6} sm={6} md={3} key={index} sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      px: { xs: 1, md: 2 }
                    }}>
                      <Paper sx={{
                        p: { xs: 2, sm: 2.5, md: 3.5 },
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderTop: '4px solid',
                        borderColor: 'primary.main',
                        transition: 'transform 0.3s ease-in-out',
                        width: '100%',
                        maxWidth: 280,
                        '&:hover': {
                          transform: 'translateY(-4px)'
                        }
                      }}>
                        <Typography 
                          variant="h3" 
                          className="font-heading"
                          sx={{
                            fontWeight: 800,
                            color: 'primary.main',
                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.5rem' },
                            mb: 1,
                            textAlign: 'center'
                          }}
                        >
                          {stat.number}
                        </Typography>
                        <Typography 
                          className="font-sans"
                          sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                            fontWeight: 500,
                            textAlign: 'center'
                          }}
                        >
                          {stat.text}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section - Mobile Optimized */}
      <Box sx={{ bgcolor: 'grey.100', py: { xs: 4, sm: 6, md: 8 }, px: { xs: 1, sm: 2, md: 0 } }}>
        <Container>
          <Typography 
            variant="h3" 
            align="center" 
            className="font-heading"
            sx={{ 
              fontWeight: 800,
              color: 'primary.main',
              mb: 1.5,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              letterSpacing: '-0.01em'
            }}
          >
            Our Legal Services
          </Typography>
          <Typography
            align="center"
            className="font-sans"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
              mb: { xs: 3, md: 4 },
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            Comprehensive legal solutions tailored to protect and grow your business
          </Typography>
          
          <Box sx={{ 
            '.slick-slide': { 
              px: { xs: 1, md: 2 }
            },
            '.slick-prev, .slick-next': {
              '&:before': {
                color: 'primary.main',
                fontSize: { xs: '20px', md: '24px' }
              }
            },
            '.slick-prev': {
              left: { xs: '-25px', md: '-35px' }
            },
            '.slick-next': {
              right: { xs: '-25px', md: '-35px' }
            },
            '.slick-dots': {
              bottom: { xs: '-28px', md: '-35px' }
            },
            mb: { xs: 2, md: 0 }
          }}>
            <Slider {...serviceSliderSettings}>
              {services.map((service, index) => (
                <div key={index} style={{ height: '100%' }}>
                  <Card sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease-in-out',
                    mx: { xs: 1, md: 2 },
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.12)'
                    },
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      backgroundColor: 'primary.main',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8
                    }
                  }}>
                    <CardContent sx={{ 
                      p: { xs: 2, sm: 3, md: 4 },
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      <Typography 
                        variant="h5" 
                        className="font-heading"
                        sx={{ 
                          fontWeight: 700,
                          color: 'primary.main',
                          mb: 2.5,
                          fontSize: { xs: '0.95rem', sm: '1rem', md: '1.15rem' },
                          position: 'relative',
                          pb: 2,
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '60px',
                            height: '2px',
                            backgroundColor: 'primary.light'
                          }
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography 
                        className="font-sans"
                        sx={{
                          mb: 3,
                          color: 'text.secondary',
                          lineHeight: 1.8,
                          fontSize: { xs: '0.85rem', md: '0.95rem' },
                          textAlign: 'justify',
                        }}
                      >
                        {service.description}
                      </Typography>
                      <Box sx={{ 
                        mt: 'auto',
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'grey.100',
                        flexGrow: 1
                      }}>
                        {service.features.map((feature, idx) => (
                          <Typography 
                            key={idx} 
                            variant="body2" 
                            sx={{ 
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: { xs: '0.8rem', md: '0.9rem' },
                              fontWeight: 500,
                              color: 'text.primary',
                              '&:last-child': {
                                mb: 0
                              },
                              '&:before': {
                                content: '"✓"',
                                color: 'primary.main',
                                fontWeight: 'bold',
                                mr: 1.5,
                                fontSize: '1rem'
                              }
                            }}
                          >
                            {feature}
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{ 
                        display: 'flex',
                        gap: 1.5,
                        mt: 2,
                        width: '100%',
                        flexDirection: isMobile ? 'column' : 'row'
                      }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{
                            py: 0.5,
                            px: 1,
                            textTransform: 'none',
                            fontSize: { xs: '0.75rem', md: '0.8rem' },
                            fontWeight: 500,
                            borderRadius: 1,
                            minHeight: '32px',
                            height: 'auto',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white'
                            }
                          }}
                        >
                          Learn More
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<WhatsAppIcon sx={{ fontSize: '1rem' }} />}
                          onClick={() => handleWhatsAppClick(service)}
                          sx={{ 
                            py: 0.5,
                            px: 1,
                            fontSize: { xs: '0.75rem', md: '0.8rem' },
                            fontWeight: 500,
                            textTransform: 'none',
                            borderRadius: 1,
                            minHeight: '32px',
                            height: 'auto',
                            backgroundColor: '#25D366',
                            '&:hover': { backgroundColor: '#128C7E' }
                          }}
                        >
                          Free Consultation
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </Slider>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section - Mobile Optimized */}
      <Box sx={{ py: { xs: 5, sm: 6, md: 8 }, px: { xs: 1, sm: 2, md: 0 }, bgcolor: 'white' }}>
        <Container>
          <Typography 
            variant="h3" 
            align="center" 
            className="font-heading"
            sx={{ 
              fontWeight: 800,
              color: 'primary.main',
              mb: 1.5,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              letterSpacing: '-0.01em'
            }}
          >
            Client Testimonials
          </Typography>
          <Typography
            align="center"
            className="font-sans"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
              mb: { xs: 4, md: 6 },
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            What our clients say about their experience working with us
          </Typography>
          
          <Box sx={{ 
            position: 'relative',
            '.slick-slide': { 
              px: { xs: 1, md: 2 }
            },
            '.slick-dots': {
              bottom: '-45px',
              '& li button': {
                '&:before': {
                  fontSize: '8px',
                  color: 'primary.main'
                }
              }
            },
            '.slick-prev, .slick-next': {
              zIndex: 1,
              width: { xs: '30px', md: '40px' },
              height: { xs: '30px', md: '40px' },
              '&:before': {
                color: 'primary.main',
                fontSize: { xs: '18px', md: '24px' }
              }
            },
            '.slick-prev': {
              left: { xs: '-10px', sm: '-15px', md: '-35px' }
            },
            '.slick-next': {
              right: { xs: '-10px', sm: '-15px', md: '-35px' }
            },
            pb: 5
          }}>
            <Slider {...testimonialSliderSettings}>
              {testimonials.map((testimonial, index) => (
                <div key={index}>
                  <Card sx={{ 
                    mx: { xs: 1, md: 2 },
                    height: '100%',
                    minHeight: { xs: '220px', md: '240px' },
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <CardContent sx={{ 
                      p: { xs: 2, sm: 3, md: 4 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}>
                      <Avatar sx={{ 
                        bgcolor: 'primary.main',
                        width: { xs: 50, md: 60 },
                        height: { xs: 50, md: 60 },
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                        fontWeight: 500,
                        mb: 1.2
                      }}>
                        {testimonial.name[0]}
                      </Avatar>
                      <Typography 
                        className="font-heading"
                        sx={{ 
                          fontWeight: 700,
                          color: 'text.primary',
                          fontSize: { xs: '1.1rem', md: '1.25rem' },
                          mb: 1
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography 
                        className="font-sans"
                        sx={{
                          color: 'text.secondary',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          mb: 1.5
                        }}
                      >
                        {testimonial.position}
                      </Typography>
                      <Box sx={{
                        position: 'relative',
                        width: '100%',
                        '&::before': {
                          content: 'open-quote',
                          position: 'absolute',
                          left: 0,
                          top: -16,
                          fontSize: '3rem',
                          color: 'primary.light',
                          opacity: 0.3,
                          fontFamily: 'serif'
                        }
                      }}>
                        <Typography 
                          className="font-sans"
                          sx={{ 
                            fontStyle: 'italic',
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            lineHeight: 1.7,
                            color: 'text.primary',
                            px: { xs: 3, md: 4 }
                          }}
                        >
                          {testimonial.content}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </Slider>
          </Box>
        </Container>
      </Box>

      {/* Call to Action - Mobile Optimized */}
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        py: { xs: 5, sm: 6, md: 8 },
        px: { xs: 2, md: 0 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h3" 
              className="font-heading"
              sx={{ 
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                letterSpacing: '-0.01em'
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography 
              className="font-sans"
              sx={{ 
                mb: { xs: 3, md: 4 }, 
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
                opacity: 0.9,
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Contact us today for a consultation with our expert legal team and discover how we can help protect and grow your business.
            </Typography>
            <Button 
              component={Link} 
              to="/contact" 
              variant="contained" 
              color="secondary" 
              size={isMobile ? "medium" : "large"}
              sx={{
                py: { xs: 1, md: 1.5 },
                px: { xs: 3, md: 4 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  transition: 'transform 0.3s ease'
                }
              }}
            >
              Schedule a Consultation
            </Button>
          </Box>
        </Container>
        {/* Background gradient overlay */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0) 100%)',
          zIndex: 0
        }} />
      </Box>
    </>
  );
};

export default Home;